import { useEffect, useState } from "react";
import { Spinner } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Compact ERP-style Delivery Profile panel. Read-only. Never writes to or
 * modifies any order-entry field.
 *
 * Columns (concise, per spec): Product | Tank | Type | Driver | Last Delivery
 * All columns left-aligned except Tank (right-aligned for numeric readability).
 */
export default function CustomerDeliveryProfilePanel({ accountNumber }) {
  const [state, setState] = useState({ loading: true, records: [], error: "" });

  useEffect(() => {
    if (!accountNumber) return;
    let cancelled = false;
    setState({ loading: true, records: [], error: "" });
    api
      .get(`/delivery-profiles/${encodeURIComponent(accountNumber)}`)
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, records: res.data || [], error: "" });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          loading: false,
          records: [],
          error: e?.response?.data?.detail || "Could not load delivery profile",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [accountNumber]);

  if (!accountNumber) return null;

  return (
    <div data-testid="customer-delivery-profile-panel" className="mt-4">
      <div className="mb-1.5 flex items-baseline justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          Delivery Profile
          <span className="ml-2 font-normal normal-case tracking-normal text-gray-400">
            rpt_004 · read-only
          </span>
        </div>
        {state.records.length > 0 && (
          <span
            data-testid="dp-tank-count"
            className="font-mono text-[11px] text-gray-500"
          >
            {state.records.length} tank{state.records.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {state.loading ? (
        <div className="py-2 text-xs text-gray-500">
          <Spinner size={12} className="mr-1.5 inline animate-spin" /> Loading…
        </div>
      ) : state.error ? (
        <div data-testid="dp-error" className="py-1.5 text-xs text-amber-800">
          {state.error}
        </div>
      ) : state.records.length === 0 ? (
        <div data-testid="dp-empty" className="py-1.5 text-xs text-gray-500">
          No delivery profile information available.
        </div>
      ) : (
        <table className="w-full border-collapse text-[13px] leading-tight">
          <thead>
            <tr className="border-b border-gray-300 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              <th className="py-1 pr-3 font-semibold">Product</th>
              <th className="py-1 pr-3 text-right font-semibold">Tank</th>
              <th className="py-1 pr-3 font-semibold">Type</th>
              <th className="py-1 pr-3 font-semibold">Zone</th>
              <th className="py-1 pr-3 font-semibold">Driver</th>
              <th className="py-1 pr-0 font-semibold">Last Delivery</th>
            </tr>
          </thead>
          <tbody>
            {state.records.map((r, i) => (
              <tr
                key={`${r.product_code}-${r.tank_name}-${i}`}
                data-testid={`dp-row-${r.product_code}`}
                className="border-b border-gray-100 last:border-0"
              >
                <td className="py-1 pr-3">
                  <span className="mr-1.5 font-mono text-[11px] text-gray-500">
                    {r.product_code || "—"}
                  </span>
                  <span className="text-gray-900">{r.product_name || "—"}</span>
                </td>
                <td className="py-1 pr-3 text-right font-mono tabular-nums text-gray-800">
                  {r.tank_capacity != null ? formatCapacity(r.tank_capacity) : "—"}
                </td>
                <td className="py-1 pr-3 text-gray-800">
                  {formatDeliveryType(r.delivery_type) || "—"}
                </td>
                <td className="py-1 pr-3 font-mono tabular-nums text-gray-800">
                  {r.delivery_zone || <span className="text-gray-400">—</span>}
                </td>
                <td className="py-1 pr-3 text-gray-800">
                  {r.driver_name || <span className="text-gray-400">Unassigned</span>}
                </td>
                <td className="py-1 pr-0 font-mono tabular-nums text-gray-700">
                  {r.last_delivery_date ? formatLastDelivery(r.last_delivery_date) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function formatCapacity(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `${n.toLocaleString("en-CA")} L`;
}

/**
 * Delivery Type transform (business rule):
 *   "Will Call (AUTO REPLACE)" → "Will Call"
 *   "Keep Full (Automatic)"    → "Keep Full"
 *   "Degree Day (Automatic)"   → "Degree Day"
 * Everything from the first "(" is stripped and trailing spaces trimmed.
 */
function formatDeliveryType(raw) {
  if (!raw) return "";
  const s = String(raw);
  const idx = s.indexOf("(");
  const head = idx === -1 ? s : s.slice(0, idx);
  return head.replace(/\s+$/g, "");
}

/**
 * Last-delivery date rule:
 *   • Current year  → "MMM DD"        (e.g. "Jun 24")
 *   • Prior years   → "MMM DD, YYYY"  (e.g. "Aug 17, 2017")
 */
function formatLastDelivery(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-CA", {
    month: "short",
    day: "2-digit",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
